/**
 * Le total des gemmes octroyées par le serveur (ROADMAP 0.1).
 *
 * Une seule question posée au serveur — « combien m'as-tu octroyé en tout ? » — et
 * c'est volontairement tout. La table `gem_grants` est fermée à double tour côté
 * client (RLS sans policy, privilèges révoqués) : la fonction `paid_gems_total()` est
 * son unique porte, et elle ne rend qu'un nombre. L'historique d'achat détaillé, avec
 * ses identifiants de session Stripe, n'a aucune raison de descendre dans un
 * navigateur pour afficher un solde.
 *
 * ⚠️ `null` ne veut PAS dire zéro, et l'appelant ne doit jamais le traduire ainsi.
 * Hors ligne, sans configuration Supabase, ou sur un serveur en panne, il n'y a
 * simplement rien à dire. Un zéro serait une affirmation — et
 * `reconcilePaidGems` traiterait cette affirmation comme une vérité.
 */

import { currentAuthSession, getSupabase, isSupabaseConfigured } from './supabase';

/** Total octroyé au joueur connecté, ou `null` si la question n'a pas pu être posée. */
export async function fetchPaidGemsTotal(): Promise<number | null> {
  if (!isSupabaseConfigured()) return null;

  /*
    ⚠️ L'IDENTITÉ D'ABORD, et ce n'est pas une précaution : c'est un correctif.

    `paid_gems_total()` a son `execute` révoqué à `anon` — délibérément, la table est
    fermée à double tour. Appelée avant que la session anonyme soit ouverte, elle rend
    donc **401**, et le navigateur inscrit « Failed to load resource » dans la console
    de chaque joueur. Livré en 0.54.0, trouvé en 0.54.4 par le parcours enchaîné lancé
    contre la production, qui tient la console pour contractuellement vide.

    La synchronisation ouvre bien cette session, mais en arrière-plan : au démarrage,
    la course était perdue une fois sur deux. `ensureAuthSession` partage la promesse
    en vol — on attend celle de la synchro plutôt que d'en ouvrir une seconde.
  */
  /*
    ⚠️ `currentAuthSession`, PAS `ensureAuthSession`.

    Lire le registre des achats ouvrait un compte anonyme pour découvrir qu'il n'y a
    aucun achat — et c'est logiquement à l'envers : **sans compte, il ne PEUT pas y avoir
    d'achat enregistré**, puisque le registre est indexé par propriétaire. On mintait donc
    une identité, à chaque démarrage, pour interroger une table qui ne pouvait rien rendre.

    Aucune régression pour le joueur qui a payé : c'est sa session QUI EST son identité de
    paiement. S'il l'a perdue, `ensureAuthSession` lui en aurait donné une nouvelle — donc
    vide — et n'aurait rien récupéré non plus.
  */
  if ((await currentAuthSession()) === null) return null;

  try {
    const { data, error } = await getSupabase().rpc('paid_gems_total');
    if (error !== null) return null;

    // La somme remonte en `bigint` côté Postgres. PostgREST la rend en nombre tant
    // qu'elle tient, mais on ne le suppose pas (règle 14) : une chaîne se convertit,
    // et tout ce qui n'est ni l'un ni l'autre est traité comme « rien à dire ».
    const total = typeof data === 'string' ? Number.parseInt(data, 10) : data;
    return typeof total === 'number' && Number.isFinite(total) && total >= 0 ? total : null;
  } catch {
    return null;
  }
}
